import { formatDuration } from '@/helpers/formatTime';
import { Box, Typography } from '@mui/material';
import { audioProgressBarStyles } from './styles';

interface AudioProgressBarProps {
  audioSrc: string;
  isProcessingAudio: boolean;
  isReady: boolean;
  audioDuration: number;
  currentTime: number;
  isPlaying: boolean;
  onSeek: (time: number) => void;
}

const AudioProgressBar: React.FC<AudioProgressBarProps> = ({
  audioSrc,
  isProcessingAudio,
  isReady,
  audioDuration,
  currentTime,
  isPlaying,
  onSeek,
}) => {
  const getProgressPercentage = () => {
    if (audioDuration === 0) return 0;
    return (currentTime / audioDuration) * 100;
  };

  const handleProgressBarClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Block seeking until metadata loaded (duration known)
    if (!audioSrc || !isReady) return;

    const progressBar = event.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const progressBarWidth = rect.width;

    // Calculate the percentage of the dark purple section (audio duration relative to 1 hour)
    const audioDurationPercentage = Math.min((audioDuration / 3600) * 100, 100);
    const darkPurpleWidth = (audioDurationPercentage / 100) * progressBarWidth;

    // Only allow seeking within the dark purple section
    if (clickX <= darkPurpleWidth) {
      const seekPercentage = clickX / darkPurpleWidth;

      // If clicking very close to the start (within 5% of the bar), start from beginning
      const seekTime =
        seekPercentage < 0.05 ? 0 : seekPercentage * audioDuration;
      onSeek(seekTime);
    }
  };
  if (!audioSrc && !isProcessingAudio) return null;

  return (
    <Box sx={audioProgressBarStyles.container}>
      <Box
        sx={{
          ...audioProgressBarStyles.progressBar,
          cursor: isProcessingAudio || !isReady ? 'default' : 'pointer',
          opacity: isReady ? 1 : 0.6,
        }}
        onClick={isProcessingAudio || !isReady ? undefined : handleProgressBarClick}
      >
        {/* Audio length indicator (dark purple) - shows duration relative to 1 hour */}
        <Box
          sx={{
            ...audioProgressBarStyles.audioDurationBar,
            width: isProcessingAudio
              ? '30%'
              : `${Math.min((audioDuration / 3600) * 100, 100)}%`,
          }}
        />
        {/* Current playback position (light purple) */}
        {(isPlaying || (!isPlaying && currentTime > 0)) && (
          <Box
            sx={{
              ...audioProgressBarStyles.playbackPositionBar,
              width: `${(getProgressPercentage() / 100) * Math.min((audioDuration / 3600) * 100, 100)}%`,
            }}
          />
        )}
      </Box>
      <Typography variant="body2" sx={audioProgressBarStyles.timeText}>
        {currentTime > 0
          ? `${formatDuration(Math.floor(currentTime))} / ${formatDuration(audioDuration)}`
          : formatDuration(audioDuration)}
      </Typography>
    </Box>
  );
};

export { AudioProgressBar };
